/**
 * 변경 이력 — 저장할 때마다 남은 판을 보고, 앞 판으로 되돌린다.
 *
 * 되돌리기는 평소 수정과 같은 검사를 지난다 — 그 판이 가리키던 콘텐츠가 지워졌으면 수정과 같은 까닭으로
 * 막힌다. 되돌린 것도 새 판이 되므로, 잘못 되돌렸으면 다시 앞으로 돌리면 된다.
 */
import { useState, type ReactNode } from 'react';
import { History, RotateCcw } from 'lucide-react';
import type { HistoryType, SignageOneClient } from '../api/client.js';
import { useAction, useAsync } from '../hooks.js';
import { Badge, ErrorNote, Loading, Modal, relativeTime } from './ui.js';
import { confirmDialog } from './ConfirmDialog.js';

const REASON_LABEL = { create: '처음 만듦', update: '고침', restore: '되돌림' } as const;

/** 수행자 표기에서 종류 접두사를 뗀다 — 활동 로그와 같은 모양으로. */
function actorName(actor: string): string {
  if (!actor) return '—';
  const at = actor.indexOf(':');
  return at >= 0 ? actor.slice(at + 1) : actor;
}

export function HistoryButton({
  client,
  type,
  id,
  canRestore,
  onRestored,
}: {
  client: SignageOneClient;
  type: HistoryType;
  id: string;
  /** 되돌릴 권한. 없으면 목록만 보인다. */
  canRestore: boolean;
  onRestored: () => void;
}): ReactNode {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button type="button" className="btn" onClick={() => setOpen(true)}>
        <History className="h-3.5 w-3.5" aria-hidden="true" /> 변경 이력
      </button>
      {open ? (
        <HistoryModal
          client={client}
          type={type}
          id={id}
          canRestore={canRestore}
          onClose={() => setOpen(false)}
          onRestored={() => {
            setOpen(false);
            onRestored();
          }}
        />
      ) : null}
    </>
  );
}

export function HistoryModal({
  client,
  type,
  id,
  canRestore,
  onClose,
  onRestored,
}: {
  client: SignageOneClient;
  type: HistoryType;
  id: string;
  canRestore: boolean;
  onClose: () => void;
  onRestored: () => void;
}): ReactNode {
  const history = useAsync(() => client.history(type, id), [client, type, id]);
  const action = useAction();
  const items = history.data?.items ?? [];
  const latest = items[0]?.version;

  return (
    <Modal title="변경 이력" onClose={onClose}>
      <div className="space-y-3">
        {action.error ? <ErrorNote message={action.error} onDismiss={action.clearError} /> : null}
        {history.error ? <ErrorNote message={history.error} /> : null}
        {history.loading && !history.data ? <Loading /> : null}
        {history.data && items.length === 0 ? (
          <p className="hint">남은 판이 없습니다. 이 기능이 생기기 전에 만든 것이면 다음에 저장할 때부터 남습니다.</p>
        ) : null}
        <p className="hint">저장할 때마다 한 판이 남습니다(최근 50판). 되돌린 것도 새 판이 됩니다.</p>
        <ul className="divide-y divide-slate-100 rounded-md border border-slate-200">
          {items.map((item) => (
            <li key={item.version} className="flex flex-wrap items-center gap-2 px-3 py-2 text-[13px]">
              <span className="mono w-10 shrink-0 text-slate-500">#{item.version}</span>
              <span className="min-w-0 flex-1">
                <span className="block truncate text-slate-800">{item.summary}</span>
                <span className="block text-[11.5px] text-slate-400">
                  {REASON_LABEL[item.reason]} · {actorName(item.actor)} ·{' '}
                  <span title={new Date(item.created_at).toLocaleString('ko-KR')}>{relativeTime(item.created_at)}</span>
                </span>
              </span>
              {item.version === latest ? (
                <Badge tone="ok">지금</Badge>
              ) : canRestore ? (
                <button
                  type="button"
                  className="btn btn-tiny"
                  disabled={action.pending}
                  onClick={async () => {
                    const ok = await confirmDialog({
                      title: `#${item.version} 판으로 되돌릴까요?`,
                      body: '화면에 바로 반영됩니다. 지금 판은 이력에 남습니다.',
                      confirmLabel: '되돌리기',
                    });
                    if (!ok) return;
                    void action.run(
                      () => client.restoreRevision(type, id, item.version),
                      onRestored,
                      `#${item.version} 판으로 되돌렸습니다`,
                    );
                  }}
                >
                  <RotateCcw className="h-3 w-3" aria-hidden="true" /> 되돌리기
                </button>
              ) : null}
            </li>
          ))}
        </ul>
      </div>
    </Modal>
  );
}
